import React, { useState } from 'react'
import styled from 'styled-components'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import Checkbox from 'components/atoms/Checkbox'
import { Link } from 'react-router-dom'
import { PRIVATE_PATHS } from 'utils/paths'
import { pathOr, propOr } from 'ramda'
import PaymentButton from 'features/cart/PaymentButton'
import CancelIcon from '@mui/icons-material/Cancel'
import { isNotNilOrEmpty } from 'utils/ramda'

const CartSummary = ({ prices, items = [] }) => {
  const [accepted, setAccepted] = useState(false)
  const hasItems = isNotNilOrEmpty(items)
  const toggleCheckbox = () => setAccepted(prev => !prev)

  const hasDiscount = propOr(0, 'percentageDiscount', prices) > 0
  const hasAnnualSubscriptions = items.some(
    item => item.billingType === 'subscription' && item.billingInterval === 'year'
  )
  const hasOneTimeItems = items.some(item => item.billingType === 'one_time')

  const countVat = () => {
    const netPrice = hasDiscount
      ? pathOr(0, ['discountedNetPrice', 'float'], prices)
      : pathOr(0, ['netPrice', 'float'], prices)
    const grossPrice = pathOr(0, ['grossPrice', 'float'], prices)
    return (grossPrice - netPrice).toFixed(2)
  }

  return (
    <Wrapper>
      {
        window.location.pathname.includes('failed') && (
          <PaymentErrorWrapper>
            <ErrorIcon />
            Payment failed! Try again.
          </PaymentErrorWrapper>
        )
      }
      <ItemRow>
        <Label>Cart total</Label>
        <CartTotalPrice>
          {
            hasDiscount
              ? pathOr('--', ['discountedNetPrice', 'formatted'], prices)
              : pathOr('--', ['netPrice', 'formatted'], prices)
          }
        </CartTotalPrice>
      </ItemRow>
      <ItemRow>
        <Label>vat</Label>
        <VatValue>{countVat()}</VatValue>
      </ItemRow>
      {
        hasDiscount && (
          <DiscountWrapper>
            <ItemRow>
              <Label>discount</Label>
              <DiscountValue>
                {`-${propOr(0, 'percentageDiscount', prices)}%`}
              </DiscountValue>
            </ItemRow>
          </DiscountWrapper>
        )
      }
      <Separator />
      <ItemRow>
        <Label>
          total amount
        </Label>
        <TotalPrice>
          {pathOr('--', ['grossPrice', 'formatted'], prices)}
        </TotalPrice>
      </ItemRow>
      <InputDescription>
        VAT included in the total
      </InputDescription>
      {hasItems && (
        <BillingNotice>
          <BillingNoticeTitle>
            {hasAnnualSubscriptions
              ? hasOneTimeItems
                ? 'Annual subscription + one-time payment'
                : 'Annual subscription'
              : 'One-time payment'}
          </BillingNoticeTitle>
          <BillingNoticeText>
            {hasAnnualSubscriptions
              ? hasOneTimeItems
                ? 'Annual items renew automatically every year until cancelled. Lifetime items are charged only once.'
                : 'The total is charged today and renews automatically every year until cancelled.'
              : 'This is charged once and does not renew automatically.'}
          </BillingNoticeText>
        </BillingNotice>
      )}
      <Separator small />
      <Checkbox
        value={accepted}
        disabled={!hasItems}
        label={
          <CheckboxLabel>
            I agree to <Link to={PRIVATE_PATHS.terms} target="_blank">Terms & Conditions</Link>
          </CheckboxLabel>
        }
        onChange={toggleCheckbox}
      />
      <PaymentButton
        accepted={accepted}
        hasItems={hasItems}
        hasAnnualSubscriptions={hasAnnualSubscriptions}
        hasOneTimeItems={hasOneTimeItems}
      />
    </Wrapper>
  )
}

export default CartSummary

const Wrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 50px 30px;

  @media (max-width: 1024px) {
    padding: 20px 0;
    background-color: ${({ theme }) => theme.colors.lighterGrey};
  }
`

const Label = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.darkGrey};
  font-weight: bold;
  opacity: .5;
`

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CartTotalPrice = styled.div`
  font-size: 19px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.darkGrey};
`

const TotalPrice = styled.div`
  font-size: 19px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
`

const VatValue = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.darkGrey};
  opacity: .6;
  font-weight: bold;
`

const DiscountWrapper = styled.div`
  margin-top: 15px;
`

const DiscountValue = styled.div`
  font-size: 13px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.haiti};
`

const Separator = styled.div`
  width: 100%;
  height: ${({ small }) => small ? '2px' : '4px'};
  margin: 25px 0;
  background-color: ${({ theme }) => theme.colors.grey[500]};
`

const PaymentErrorWrapper = styled.div`
  display: flex;
  font-size: 18px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.error};
  align-items: center;
  justify-content: center;
  margin-bottom: 45px;
`

const CheckboxLabel = styled.div`
  font-size: 14px;

  a {
    color: ${({ theme }) => theme.colors.secondary};
  }
`

const BillingNotice = styled.div`
  margin-top: 18px;
  padding: 12px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #e6f7f9;
`

const BillingNoticeTitle = styled.div`
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.haiti};
`

const BillingNoticeText = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.darkGrey};
`

const ErrorIcon = styled(CancelIcon)`
  font-size: 24px !important;
  margin-right: 10px;
`
